import ButtonLink from '../ButtonLink';
import EmailSent from '../EmailSent';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Heading from '@atlaskit/heading';
import OtpInput from '../OtpInput';
import React, { useEffect, useMemo, useState } from 'react';
import TallButton from '../TallButton';
import Whitebox from '../WhiteboxPage';
import messages from './VerifyEmailOtp.messages';
import styled from '@emotion/styled';
import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { OTP_CODE_FULL_LENGTH } from './constants';
import { colors, gridSize } from '@atlaskit/theme';
import { captureErrorMessage } from '../../utilities/analytics/error-reporting';

type VerifyEmailOtpFlow = 'sign-up' | 'login-challenge';

export type VerifyEmailOtpErrorCode =
  | 'invalid_otp'
  | 'invalid_otp_length'
  | 'invalid_token'
  | 'expired_token'
  | 'rate_limited'
  | 'unexpected_error';

export type VerifyEmailOtpProps = {
  pageId: string;
  flow: VerifyEmailOtpFlow;
  email: string;
  isLoading: boolean;
  errorCode?: VerifyEmailOtpErrorCode;
  additionalWhiteboxFooter?: React.ReactNode;
  onVerifyEmailOtp: (otp: string) => void;
  onResendEmailOtp: () => void;
  onInvalidEmailOtpLength?: (otp: string) => void;
};

const OtpError = styled.div`
  margin-top: 4px;
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;
  font-size: 12px !important;
  color: ${colors.R500};
`;

const OtpInputWrapper = styled.div`
  display: flex;
  align-content: center;
  justify-content: center;
  padding: ${gridSize() * 3}px 0 0 0;
`;

const EMPTY_OTP_STATE = ' '.repeat(OTP_CODE_FULL_LENGTH);

const removePlaceholders = (otp: string) => otp.replace(/ /g, '');

export const VerifyEmailOtp = ({
  pageId,
  flow,
  email,
  isLoading,
  errorCode,
  additionalWhiteboxFooter: footer,
  onVerifyEmailOtp,
  onResendEmailOtp,
  onInvalidEmailOtpLength,
}: VerifyEmailOtpProps) => {
  const [otp, setOtp] = useState<string>(EMPTY_OTP_STATE);
  const [isInvalidOtpLength, setInvalidOtpLength] = useState<boolean>(false);

  const { formatMessage } = useIntl();

  const handleOtpChange = (otp: string) => {
    setOtp(otp);
    const sanitisedOtp = removePlaceholders(otp);
    if (sanitisedOtp.length === OTP_CODE_FULL_LENGTH) {
      setInvalidOtpLength(false);
      onVerifyEmailOtp(sanitisedOtp);
    }
  };

  const handleSubmit = () => {
    const sanitisedOtp = removePlaceholders(otp);
    if (sanitisedOtp.length === OTP_CODE_FULL_LENGTH) {
      setInvalidOtpLength(false);
      onVerifyEmailOtp(sanitisedOtp);
    } else {
      setInvalidOtpLength(true);
      onInvalidEmailOtpLength?.(sanitisedOtp);
    }
  };

  const otpMessageDescriptor = useMemo(() => {
    switch (flow) {
      case 'sign-up':
        return messages.signUpOtpMessage;
      case 'login-challenge':
        return messages.loginChallengeOtpMessage;
      default:
        captureErrorMessage(
          `No OTP message defined for VerifyEmailOtp flow "${flow}", falling back to default message`
        );
        return messages.defaultOtpMessage;
    }
  }, [flow]);

  const errorMessageDescriptor = useMemo(() => {
    if (isInvalidOtpLength) {
      return messages.invalidOtpLengthErrorMessage;
    }
    if (!errorCode) {
      return undefined;
    }

    switch (errorCode) {
      case 'invalid_otp':
        return messages.invalidOtpErrorMessage;
      case 'invalid_otp_length':
        return messages.invalidOtpLengthErrorMessage;
      case 'invalid_token':
        return messages.invalidTokenErrorMessage;
      case 'expired_token':
        return messages.expiredTokenErrorMessage;
      case 'rate_limited':
        return messages.rateLimitedErrorMessage;
      case 'unexpected_error':
        return messages.unexpectedErrorErrorMessage;
      default:
        return messages.unexpectedErrorErrorMessage;
    }
  }, [errorCode, isInvalidOtpLength]);

  useEffect(() => {
    if (errorCode === 'invalid_otp') {
      setOtp(EMPTY_OTP_STATE);
    }
  }, [errorCode]);

  return (
    <Whitebox
      pageId={pageId}
      header={
        <Heading as="h1" size="medium">
          <FormattedMessage {...messages.headOtpTitle} />
        </Heading>
      }
      whiteboxFooter={
        <>
          <ButtonLink id="otp-resend" spacing="none" onClick={onResendEmailOtp}>
            {formatMessage(messages.resendOtpButton)}
          </ButtonLink>
          {footer}
        </>
      }>
      <Helmet title={formatMessage(messages.headOtpTitle)} />
      <EmailSent
        email={email}
        message={formatMessage(otpMessageDescriptor)}
        withEmailSentImage={false}
      />
      <OtpInputWrapper>
        <OtpInput
          otpCodeFullLength={OTP_CODE_FULL_LENGTH}
          onChange={handleOtpChange}
          otp={otp}
          isLoading={isLoading}
          isError={!!errorCode}
        />
      </OtpInputWrapper>
      {!!errorMessageDescriptor && (
        <OtpError>
          <ErrorIcon label={'Error message icon'} size={'small'} />
          {formatMessage(errorMessageDescriptor)}
        </OtpError>
      )}
      <TallButton
        id="otp-submit"
        appearance={'primary'}
        type="submit"
        isFullWidth
        style={{ marginTop: '30px' }}
        onClick={handleSubmit}
        isLoading={isLoading}>
        {formatMessage(messages.submitButton)}
      </TallButton>
    </Whitebox>
  );
};

export default VerifyEmailOtp;
